import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core'
import {
    CalendarDayAvailablePartner,
    CalendarDayBookedGroupMeeting,
    CalendarDayComponent,
    CalendarDayBookedSession,
    CalendarDayData,
    CalendarDaySelectedSession,
} from '../calendar-day/calendar-day.component'
import { TimeslotLabelsComponent } from '../timeslot-labels/timeslot-labels.component'
import { CommonModule } from '@angular/common'
import {
    AvailableTimesAndPartnersLists,
    AvailableTimesService,
} from '@app/core/_services/available-times.service'
import { filter, Subject, take, takeUntil } from 'rxjs'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import {
    MeetingsParticipantsMap,
    MeetingsParticipantInfo,
    MeetingsBookingsGroupMeeting,
    MeetingsBookingsMeetingItem,
    BookSessionPreferences,
    ConfirmedSessionsResponse,
    PartnerSessionPreferences,
    SESSION_ACTIVITY_TYPE_ANYTHING,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    TimeFormat,
    User,
} from '@app/core/_models'
import {
    ConfirmedSessionsService,
    FeatureCheckService,
    FIFTEEN_MINS_IN_MSECS,
    MeetingsService,
    SelectedSession,
    SelectedSessionService,
    UtilsService,
} from '@app/core/_services'
import { UserService } from '@app/core/_services/user.service'
import { EpochToTimePipe } from '@app/core/pipes/epoch-to-time.pipe'
import type { DayData } from '../calendar/calendar.component'
import { DomSanitizer, SafeStyle } from '@angular/platform-browser'
import { ContentStackService } from '@app/core/_services/content-stack.service'
import { CalendarViewService } from '../../services/calendar-view.service'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { FmWindowResizedDirective } from '@app/shared/directives/fm-window-resized.directive'
import { TooltipModule } from 'primeng/tooltip'
import { DstBoundaryService } from '@app/core/_services/dst-service'
import { GroupMeetingCardVm } from '@app/core/_services/group-meetings-store.service'

// 24hours, 4 slots per hour (:00,:15,:30,:45) + 3 slots
// at the end in case a session tile needs to be placed
// at the end of the day
const NUM_TIMESLOTS_PER_DAY = 24 * 4 + 3
const TIMESLOT_HEIGHT = 48 // Each 15minutes slot is 48px high

@Component({
    selector: 'fm-calendar-days',
    imports: [
        TooltipModule,
        CalendarDayComponent,
        TimeslotLabelsComponent,
        CommonModule,
        EpochToTimePipe,
        FmWindowResizedDirective,
    ],
    templateUrl: './calendar-days.component.html',
    styleUrl: './calendar-days.component.scss',
})
export class CalendarDaysComponent {
    @ViewChild('scrollPanel', { read: ElementRef, static: true }) scrollPanel: ElementRef

    @Input() days: Array<DayData> = new Array<DayData>(0)
    @Input() timezone: string
    @Input() timeFormat: TimeFormat = '12hr'
    @Input() sessionDuration: number
    @Input() availableGroupMeetings: GroupMeetingCardVm[] = []
    @Output() updatedScrollbarWidth = new EventEmitter<number>()
    public selectedSessionData: SelectedSession[]
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private availableTimesAndPartnersFullList: AvailableTimesAndPartnersLists
    private bookedSessionsFullList: CalendarDayBookedSession[] = []
    private bookedGroupMeetingsFullList: CalendarDayBookedGroupMeeting[] = []

    public calendarDayData: CalendarDayData[] = []

    // Temporary until we figure out ghosting outline issue
    // with PrimeNg Scroll panel
    public toolbarWidth: SafeStyle
    private scrollBarWidth: number
    private scrollpositionInitialized: boolean = false
    public timeZoneAbbr: string
    public timezoneTooltip: string

    constructor(
        private sanitizer: DomSanitizer,
        private utilsService: UtilsService,
        private confirmedSessionService: ConfirmedSessionsService,
        private meetingsService: MeetingsService,
        private featureCheckService: FeatureCheckService,
        private userService: UserService,
        private selectedSessionService: SelectedSessionService,
        private availableTimesService: AvailableTimesService,
        private contentStackService: ContentStackService,
        private calendarViewService: CalendarViewService,
        private timeUtils: TimeUtilitiesService,
        private dstService: DstBoundaryService,
    ) {}

    ngOnInit() {
        this.availableTimesService.availableTimesAndPartners
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((availableTimesAndPartners: AvailableTimesAndPartnersLists) => {
                this.availableTimesAndPartnersFullList = availableTimesAndPartners
                this.buildDayData()
            })

        this.selectedSessionService.selectedSessions$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedSessions: SelectedSession[]) => {
                this.selectedSessionData = [...selectedSessions]
                this.buildDayData()
            })

        this.meetingsService.meetingsBookingsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((eventsBookingsState) => {
                this.bookedSessionsFullList = this.mapBookingEventsToBookedSessions(
                    eventsBookingsState.meetings,
                    eventsBookingsState.participants,
                )
                this.bookedGroupMeetingsFullList = this.mapBookingEventsToBookedGroupMeetings(
                    eventsBookingsState.meetings,
                    eventsBookingsState.participants,
                )
                this.buildDayData()
            })

        this.contentStackService.panelToggled$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            // Force re-render as the day widths will have changed but no change detection would occur
            this.buildDayData()
        })

        this.calendarViewService.todaySelected$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.updateScrollPosition()
            })

        // every minute, update the day data
        this.timeUtils
            .onMinuteStart()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.buildDayData()
            })
        this.timeZoneAbbr = moment.tz(this.timezone).format('z')
        this.timezoneTooltip = `${this.timezone.replace('_', ' ')} (${this.timeZoneAbbr})`
    }

    ngOnChanges(changes: SimpleChanges) {
        const previousDays = changes.days?.previousValue
        if (previousDays) {
            const currentDays = changes.days.currentValue

            // for each currentDay, if it's after the last day of the previous days
            // slide it in towards the left. otherwise, if it's before the first day of the previous days
            // slide it in towards the right. otherwise, don't slide.
            for (let day of currentDays) {
                if (day.startOfDayInMs > previousDays[previousDays.length - 1].startOfDayInMs) {
                    day.slideDirection = 'left'
                } else if (day.startOfDayInMs < previousDays[0].startOfDayInMs) {
                    day.slideDirection = 'right'
                }
            }
        }

        this.buildDayData()
    }

    private getScrollBarWidth(): number {
        if (this.scrollPanel) {
            const element = this.scrollPanel.nativeElement
            // Use integer layout values to avoid fractional resize jitter during change detection.
            return element.offsetWidth - element.clientWidth
        }
        return 0
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.updateScrollbarWidth()
            // Wait for the view to initialize to ensure the scrollPanel is available
            this.updateScrollPosition(false)
            this.scrollpositionInitialized = true
        })
    }

    onWindowResized(event): void {
        setTimeout(() => {
            this.updateScrollbarWidth()
        })
    }

    private updateScrollPosition(animate: boolean = true): void {
        // Cancel any pending scroll
        if (!this.scrollpositionInitialized && animate) {
            // Updating while the animation is ongoing can result in the
            // wrong position
            return
        }

        setTimeout(() => {
            if (this.scrollPanel) {
                const now = this.utilsService.getCurrentTimeForTimezone(this.timezone)
                const hour = now.hour()
                const minutes = now.minutes()

                const additional_scroll = minutes < 15 ? TIMESLOT_HEIGHT : 0
                const targetTop = hour * TIMESLOT_HEIGHT * 4 - additional_scroll

                this.scrollPanel.nativeElement.scrollTo({
                    top: targetTop,
                    behavior: animate ? 'smooth' : 'auto',
                })

                this.updateScrollbarWidth()
            }
        })
    }

    private updateScrollbarWidth(): void {
        const scrollBarWidth = this.getScrollBarWidth()
        // Ignore browser rounding oscillation between adjacent pixel values.
        if (
            this.scrollBarWidth !== undefined &&
            Math.abs(scrollBarWidth - this.scrollBarWidth) <= 1
        ) {
            return
        }

        this.scrollBarWidth = scrollBarWidth
        const thewidth: string = `calc(100% - ${this.scrollBarWidth}px)`
        this.toolbarWidth = this.sanitizer.bypassSecurityTrustStyle(thewidth)
        this.updatedScrollbarWidth.emit(this.scrollBarWidth)
    }

    private buildDayData() {
        const dayData = new Array<CalendarDayData>(0)
        const potentialPartnerDatas = this.populateDayAvailabilityData()
        const bookedSessionDatas = this.populateConfirmedSessionData()
        const bookedGroupMeetingDatas = this.populateBookedGroupMeetingData()
        const selectedSessionDatas = this.populateSelectedSessionData()

        for (let dayIndex in this.days) {
            dayData.push({
                startOfDay: this.days[dayIndex].startOfDayInMs,
                potentialPartnerData: potentialPartnerDatas[dayIndex],
                bookedSessionData: bookedSessionDatas[dayIndex],
                bookedGroupMeetingData: bookedGroupMeetingDatas[dayIndex],
                selectedSessionData: selectedSessionDatas[dayIndex],
            })
        }
        this.calendarDayData = [...dayData]
    }

    private getSessionMapForDuration() {
        if (this.sessionDuration === SESSION_DURATION_25_MINS) {
            return this.availableTimesAndPartnersFullList.sessions25minutes
        } else if (this.sessionDuration === SESSION_DURATION_50_MINS) {
            return this.availableTimesAndPartnersFullList.sessions50minutes
        } else {
            return this.availableTimesAndPartnersFullList.sessions75minutes
        }
    }

    private populateDayAvailabilityData() {
        const calendarDayAvailablePartnersArray: CalendarDayAvailablePartner[][] = new Array(
            this.days.length,
        )
            .fill(null)
            .map(() => new Array<CalendarDayAvailablePartner>())
        const groupMeetingsByStart = this.buildAvailableGroupMeetingsByStart()
        const pairedAvailabilityByStart = this.availableTimesAndPartnersFullList
            ? this.getSessionMapForDuration()
            : new Map<number, never>()

        const sessionTimes = new Set<number>([
            ...pairedAvailabilityByStart.keys(),
            ...groupMeetingsByStart.keys(),
        ])

        for (const sessionTime of sessionTimes) {
            const timeSlotData = pairedAvailabilityByStart.get(sessionTime)
            const availableGroupMeetings = groupMeetingsByStart.get(sessionTime) ?? []
            const dayIndeces = this.getDayIndexForSessionTime(sessionTime)
            for (let dayIndex of dayIndeces) {
                calendarDayAvailablePartnersArray[dayIndex].push({
                    slot: this.determineSlotNumber(sessionTime, dayIndex),
                    sessionTime,
                    sessionDuration: this.sessionDuration,
                    partner: timeSlotData?.partner ?? null,
                    hasDirectBookablePartner: timeSlotData?.hasDirectBookablePartner ?? false,
                    availableGroupMeetings,
                    hasAvailableGroupMeeting: availableGroupMeetings.length > 0,
                })
            }
        }

        return calendarDayAvailablePartnersArray
    }

    private buildAvailableGroupMeetingsByStart(): Map<number, GroupMeetingCardVm[]> {
        const groupMeetingsByStart = new Map<number, GroupMeetingCardVm[]>()

        if (!this.featureCheckService.groupMeetingsEnabledSig()) {
            return groupMeetingsByStart
        }

        for (const groupMeeting of this.availableGroupMeetings ?? []) {
            if (
                groupMeeting.state !== 'available' ||
                groupMeeting.durationMs !== this.sessionDuration
            ) {
                continue
            }

            const existingGroupMeetings = groupMeetingsByStart.get(groupMeeting.startMs) ?? []
            existingGroupMeetings.push(groupMeeting)
            groupMeetingsByStart.set(groupMeeting.startMs, existingGroupMeetings)
        }

        return groupMeetingsByStart
    }

    private populateConfirmedSessionData(): CalendarDayBookedSession[][] {
        const bookedSessionDatas: CalendarDayBookedSession[][] = new Array(this.days.length)
            .fill(null)
            .map(() => new Array<CalendarDayBookedSession>(0))

        if (this.bookedSessionsFullList) {
            for (const sessionInfo of this.bookedSessionsFullList) {
                if (
                    !this.utilsService.shouldDisplaySession(
                        sessionInfo.sessionType,
                        sessionInfo.startMs,
                        sessionInfo.state,
                        !!sessionInfo.partnerId,
                    )
                ) {
                    continue
                }

                const dayIndeces = this.getDayIndexForSessionTime(sessionInfo.startMs)
                for (let dayIndex of dayIndeces) {
                    bookedSessionDatas[dayIndex].push({
                        ...sessionInfo,
                        slot: this.determineSlotNumber(sessionInfo.startMs, dayIndex),
                    })
                }
            }
        }

        return bookedSessionDatas
    }

    private populateBookedGroupMeetingData(): CalendarDayBookedGroupMeeting[][] {
        const bookedGroupMeetingDatas: CalendarDayBookedGroupMeeting[][] = new Array(
            this.days.length,
        )
            .fill(null)
            .map(() => new Array<CalendarDayBookedGroupMeeting>(0))

        if (this.bookedGroupMeetingsFullList) {
            for (const groupMeetingInfo of this.bookedGroupMeetingsFullList) {
                const dayIndeces = this.getDayIndexForSessionTime(groupMeetingInfo.startMs)
                for (const dayIndex of dayIndeces) {
                    bookedGroupMeetingDatas[dayIndex].push({
                        ...groupMeetingInfo,
                        slot: this.determineSlotNumber(groupMeetingInfo.startMs, dayIndex),
                    })
                }
            }
        }

        return bookedGroupMeetingDatas
    }

    private mapBookingEventsToBookedSessions(
        meetings: MeetingsBookingsMeetingItem[],
        participants: MeetingsParticipantsMap,
    ): CalendarDayBookedSession[] {
        const mappedSessions: CalendarDayBookedSession[] = []

        for (const meeting of meetings) {
            if (meeting.meetingType !== 'paired') {
                continue
            }

            const partner = meeting.partnerId ? participants[meeting.partnerId] : null
            mappedSessions.push({
                ...meeting,
                slot: 0,
                partner,
            })
        }

        return mappedSessions
    }

    private mapBookingEventsToBookedGroupMeetings(
        meetings: MeetingsBookingsMeetingItem[],
        participantsMap: MeetingsParticipantsMap,
    ): CalendarDayBookedGroupMeeting[] {
        const mappedGroupMeetings: CalendarDayBookedGroupMeeting[] = []
        for (const meeting of meetings) {
            if (meeting.meetingType !== 'group_meeting') {
                continue
            }

            const participants = this.mapGroupMeetingParticipants(meeting, participantsMap)
            // const participantsMap = participantInfos.reduce((acc, participant) => {
            //     acc[participant.id] = participant
            //     return acc
            // }, {} as MeetingsParticipantsMap)

            mappedGroupMeetings.push({
                ...meeting,
                slot: 0,
                participants,
            })
        }
        return mappedGroupMeetings
    }

    private mapGroupMeetingParticipants(
        meeting: MeetingsBookingsGroupMeeting,
        participantsMap: MeetingsParticipantsMap,
    ): MeetingsParticipantInfo[] {
        const participantConfigs = Array.isArray(meeting.participantConfigs)
            ? meeting.participantConfigs
            : []

        return participantConfigs.map((participant) => {
            return this.resolveParticipantInfo(participant.id, participantsMap)
        })
    }

    private resolveParticipantInfo(
        participantId: string,
        participantsMap: MeetingsParticipantsMap,
    ): MeetingsParticipantInfo {
        return participantsMap[participantId] ?? this.buildFallbackParticipantInfo(participantId)
    }

    private buildFallbackParticipantInfo(participantId: string): MeetingsParticipantInfo {
        return {
            id: participantId,
            displayName: null,
            profileUrl: null,
            photo: null,
            saved: false,
            status: 0,
            timezone: null,
            group: null,
            completedSessions: null,
            snoozed: false,
            snoozeExpiresAt: null,
            sharedAvailability: false,
        }
    }

    private mapConfirmedSessionsToBookedSessions(
        sessions: ConfirmedSessionsResponse[],
    ): CalendarDayBookedSession[] {
        return sessions
            .filter((session) => !!session)
            .map((session) => {
                const partner: MeetingsParticipantInfo | null = session.partnerId
                    ? {
                          id: session.partnerId,
                          displayName: session.partnerDisplayName ?? '',
                          profileUrl: session.partnerProfileUrl ?? '',
                          photo: session.partnerPhoto ?? '',
                          saved: session.saved ?? false,
                          status: session.status ?? 0,
                          timezone: session.partnerTimezone ?? null,
                          group: null,
                          completedSessions: session.user_comp_sessions ?? null,
                          snoozed: session.snoozed ?? false,
                          snoozeExpiresAt: session.snoozeExpiresAt ?? null,
                          sharedAvailability: session.sharedAvailability ?? false,
                      }
                    : null

                return {
                    meetingType: 'paired',
                    id: session.externalId ?? '',
                    startMs: session.sessionTime,
                    durationMs: session.duration,
                    title: session.title ?? null,
                    meetingRoom: session.meetingRoom ?? null,
                    partnerId: session.partnerId ?? null,
                    sessionType: session.sessionType ?? 'standard',
                    state: session.state ?? 0,
                    preferences: session.preferences ?? this.defaultPreferences(),
                    activityType: session.activityType ?? SESSION_ACTIVITY_TYPE_ANYTHING,
                    partnerPreferences:
                        session.partnerPreferences ?? this.defaultPartnerPreferences(),
                    partnerActivityType:
                        session.partnerActivityType ?? SESSION_ACTIVITY_TYPE_ANYTHING,
                    ambassadorMatch: session.ambassadorMatch ?? false,
                    slot: 0,
                    partner,
                }
            })
    }

    private defaultPreferences(): BookSessionPreferences {
        return {
            favorites: { value: 'noPreference' },
            quietMode: { value: false },
        }
    }

    private defaultPartnerPreferences(): PartnerSessionPreferences {
        return {
            quietMode: { value: false },
        }
    }

    private populateSelectedSessionData() {
        const selectedSessions: CalendarDaySelectedSession[][] = new Array(this.days.length)
            .fill(null)
            .map(() => new Array<CalendarDaySelectedSession>(0))

        if (this.selectedSessionData) {
            for (const sessionInfo of this.selectedSessionData) {
                const dayIndeces = this.getDayIndexForSessionTime(sessionInfo.sessionTime)
                for (let dayIndex of dayIndeces) {
                    selectedSessions[dayIndex].push({
                        slot: this.determineSlotNumber(sessionInfo.sessionTime, dayIndex),
                        ...sessionInfo,
                    })
                }
            }
        }
        return selectedSessions
    }

    private getDayIndexForSessionTime(sessionTime: number): number[] {
        const indeces = []
        for (let i = 0; i < this.days.length; i++) {
            if (
                sessionTime >= this.days[i].startOfDayInMs - SESSION_DURATION_75_MINS &&
                sessionTime <
                    this.timeUtils.getEndOfDay(this.days[i].startOfDayInMs, this.timezone) +
                        SESSION_DURATION_75_MINS
            ) {
                indeces.push(i)
            }
        }
        return indeces
    }

    private determineSlotNumber(sessionTime: number, dayIndex: number): number {
        const dstBoundries = this.dstService.getDstBoundaries(this.timezone, sessionTime)
        let offset = 0

        if (
            dstBoundries.startMs >= this.days[dayIndex].startOfDayInMs &&
            dstBoundries.startMs <
                this.timeUtils.getEndOfDay(this.days[dayIndex].startOfDayInMs, this.timezone) &&
            sessionTime >= dstBoundries.startMs
        ) {
            // Daylight Savings occurs during this day. Since we are moving forward in time based on offset, incremenent the slot number
            // where this session should appear on the calendar accordingly.
            offset = dstBoundries.offsetDeltaMinutes / 15
        } else if (
            dstBoundries.endMs >= this.days[dayIndex].startOfDayInMs &&
            dstBoundries.endMs <
                this.timeUtils.getEndOfDay(this.days[dayIndex].startOfDayInMs, this.timezone) &&
            sessionTime >= dstBoundries.endMs
        ) {
            // Daylight Savings occurs during this day
            offset = -dstBoundries.offsetDeltaMinutes / 15
        }

        return (sessionTime - this.days[dayIndex].startOfDayInMs) / FIFTEEN_MINS_IN_MSECS + offset
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
